import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

export type SelectOption<T extends string> = {
  value: T;
  label: string;
  description?: string;
};

type DuoSelectProps<T extends string> = {
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  showCheck?: boolean;
};

function DuoSelect<T extends string>({
  value,
  options,
  onChange,
  label,
  placeholder = "Select an option",
  disabled = false,
  className = "",
  showCheck = false,
}: DuoSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const handleSelect = (nextValue: T) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {label && (
        <label className="mb-1.5 block text-xs font-extrabold uppercase tracking-wide text-gray-400">
          {label}
        </label>
      )}

      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={[
          "group flex h-11 w-full items-center justify-between gap-3 rounded-2xl border-2",
          "border-white/10 bg-indigo-950 px-3 text-left",
          "text-sm font-extrabold text-white",
          "shadow-[0_5px_0_rgba(0,0,0,0.35)]",
          "transition-[transform,box-shadow,border-color,background-color] duration-150",
          "hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/10",
          "hover:shadow-[0_6px_0_rgba(0,0,0,0.35)]",
          "active:translate-y-1 active:shadow-none",
          "focus:outline-none focus-visible:ring-4 focus-visible:ring-white/15",
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0",
        ].join(" ")}
      >
        <span className="min-w-0 truncate">
          {selectedOption?.label ?? placeholder}
        </span>

        <ChevronDown
          className={[
            "h-4 w-4 shrink-0 text-gray-400 transition-transform duration-150",
            open ? "rotate-180" : "",
          ].join(" ")}
          aria-hidden
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-2xl border-2 border-white/10 bg-gray-950 shadow-2xl shadow-black/40">
          <ul
            role="listbox"
            tabIndex={-1}
            className="max-h-64 overflow-y-auto p-1.5"
          >
            {options.map((option) => {
              const selected = option.value === value;

              return (
                <li key={option.value} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={[
                      "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left",
                      "text-sm font-bold transition-colors",
                      selected
                        ? "bg-white text-gray-950"
                        : "text-gray-300 hover:bg-white/10 hover:text-white",
                    ].join(" ")}
                  >
                    {showCheck && (
                      <span
                        className={[
                          "flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border-2",
                          selected
                            ? "border-gray-950/10 bg-gray-950 text-white"
                            : "border-white/10 bg-white/5",
                        ].join(" ")}
                        aria-hidden
                      >
                        {selected && (
                          <Check className="h-3.5 w-3.5" strokeWidth={3} />
                        )}
                      </span>
                    )}

                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.label}</span>

                      {option.description && (
                        <span
                          className={[
                            "mt-0.5 block truncate text-xs",
                            selected ? "text-gray-600" : "text-gray-500",
                          ].join(" ")}
                        >
                          {option.description}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default DuoSelect;
